import type { Module } from "~/foundation/Bootstrapper/types";
import type { HistoricalTimelineViewModel } from "../generated-types";
import { createDecadeColumn, animateDividerEntrance, type DecadeColumnController } from "./create-decade-column";
import {
	createDecadeDetailView,
	type DecadeDetailViewController,
} from "./create-decade-detail-view";
import { bindTimelineFilter } from "./create-timeline-filter";
import { bindOverviewMobileSwiper, isMobileOverview, type OverviewMobileSwiperController } from "./create-overview-mobile-swiper";
import { loadTimelineDecades } from "./timeline-data-store";
import { HOVER_ENTER_DELAY_MS, HOVER_EXIT_DELAY_MS } from "./timeline-constants";
import {
	buildTimelineUrlState,
	filterValidCategoryKeys,
	findDecadeIndexByName,
	getFilterCategoryKeysFromRoot,
	getTimelineShareUrl,
	parseTimelineUrlState,
	replaceTimelineUrlState,
	resolveDetailSelection,
} from "./timeline-url-state";
import { prefersReducedMotion } from "./timeline-utils";
import "./historical-timeline.scss";

type HistoricalTimelineLabels = {
	all?: string;
	close?: string;
	copyLink?: string;
	copied?: string;
	explore?: string;
	filters?: string;
	next?: string;
	prev?: string;
	pricePerBarrel?: string;
	population?: string;
	select?: string;
	selectLabel?: string;
	emptyYearsMessage?: string;
	resetFilters?: string;
	clearFilters?: string;
	exploreTimelineAriaLabel?: string;
	event?: string;
	eventLabel?: string;
	eventsInYear?: string;
	removeFilter?: string;
};

const HistoricalTimeline = async (element: HTMLElement, props?: HistoricalTimelineViewModel) => {
	const dataSourceId = props?.dataSourceId ?? element.dataset.datasourceId;
	const content = element.querySelector<HTMLElement>(".historical-timeline__content");
	const moduleRoot = element.closest<HTMLElement>(".historical-timeline-module") ?? element;
	const filtersRoot = element.querySelector<HTMLElement>(".historical-timeline__filters");

	if (!dataSourceId || !content) {
		return () => undefined;
	}

	const dictionary = props?.dictionary as HistoricalTimelineLabels | undefined;

	const labels: HistoricalTimelineLabels = {
		all: element.dataset.labelAll ?? dictionary?.all ?? "All",
		close: element.dataset.labelClose ?? dictionary?.close ?? "Close",
		copyLink: element.dataset.labelCopyLink ?? dictionary?.copyLink ?? "Copy link",
		copied: element.dataset.labelCopied ?? dictionary?.copied ?? "Copied",
		explore: element.dataset.labelExplore ?? dictionary?.explore ?? "Explore",
		filters: element.dataset.labelFilters ?? dictionary?.filters ?? "Filters",
		next: element.dataset.labelNext ?? dictionary?.next ?? "Next",
		prev: element.dataset.labelPrev ?? dictionary?.prev ?? "Previous",
		pricePerBarrel:
			element.dataset.labelPricePerBarrel ?? dictionary?.pricePerBarrel ?? "Price per Barrel of oil (USD)",
		population:
			element.dataset.labelPopulation ?? dictionary?.population ?? "Saudi Arabia, Population (M)",
		select: element.dataset.labelSelect ?? dictionary?.selectLabel ?? dictionary?.select ?? "Select",
		emptyYearsMessage:
			element.dataset.labelEmptyYears ??
			dictionary?.emptyYearsMessage ??
			"No years found with the current filter. Try again with another filter or clear the search.",
		resetFilters: element.dataset.labelResetFilters ?? dictionary?.clearFilters ?? "Clear filters",
		exploreTimelineAriaLabel:
			element.dataset.labelExploreTimelineAriaLabel ??
			dictionary?.exploreTimelineAriaLabel ??
			"Explore timeline decades",
		event: element.dataset.labelEvent ?? dictionary?.eventLabel ?? dictionary?.event ?? "Event",
		eventsInYear: element.dataset.labelEventsInYear ?? dictionary?.eventsInYear ?? "Events in {0}",
		removeFilter: element.dataset.labelRemoveFilter ?? dictionary?.removeFilter ?? "Remove {0}",
	};

	const initialUrlState = parseTimelineUrlState();
	const availableCategoryKeys = getFilterCategoryKeysFromRoot(filtersRoot ?? null);
	const initialCategoryKeys = filterValidCategoryKeys(
		initialUrlState.categoryKeys,
		availableCategoryKeys
	);

	const decades = await loadTimelineDecades(dataSourceId);

	if (decades.length === 0) {
		return () => undefined;
	}

	let hoveredIndex: number | null = null;
	let hoverScatterToken = 0;
	let gridEntranceToken = 1;
	let activeCategoryKeys: string[] = initialCategoryKeys;
	let selectedDecadeIndex: number | null = null;
	let detailView: DecadeDetailViewController | null = null;
	const detailViewCache = new Map<number, DecadeDetailViewController>();

	const resetFilters = () => {
		filterController.reset();
	};

	let filterController: {
		destroy: () => void;
		reset: () => void;
		setCategoryKeys: (categoryKeys: string[], notifyChange?: boolean) => void;
		getCategoryKeys: () => string[];
		getAvailableCategoryKeys: () => string[];
	} = {
		destroy: () => undefined,
		reset: () => undefined,
		setCategoryKeys: () => undefined,
		getCategoryKeys: () => [],
		getAvailableCategoryKeys: () => [],
	};

	const syncUrlFromModuleState = () => {
		const decadeName =
			selectedDecadeIndex !== null ? decades[selectedDecadeIndex]?.name ?? undefined : undefined;
		const detailState = detailView?.getState();

		replaceTimelineUrlState(
			buildTimelineUrlState({
				activeCategoryKeys,
				decadeName,
				yearName: detailState?.yearName,
				eventIndex: detailState?.eventIndex,
			})
		);
	};

	const getModuleShareUrl = () =>
		getTimelineShareUrl(
			buildTimelineUrlState({
				activeCategoryKeys,
				decadeName:
					selectedDecadeIndex !== null ? decades[selectedDecadeIndex]?.name ?? undefined : undefined,
				yearName: detailView?.getState().yearName,
				eventIndex: detailView?.getState().eventIndex,
			})
		);

	const overviewHost = document.createElement("div");
	overviewHost.className = "historical-timeline__overview";
	overviewHost.setAttribute("role", "group");
	overviewHost.setAttribute("aria-label", labels.exploreTimelineAriaLabel ?? "Explore timeline decades");
	content.appendChild(overviewHost);

	const hoverEnterTimer: { current: ReturnType<typeof setTimeout> | null } = { current: null };
	const hoverExitTimer: { current: ReturnType<typeof setTimeout> | null } = { current: null };

	const clearHoverTimers = () => {
		if (hoverEnterTimer.current) {
			clearTimeout(hoverEnterTimer.current);
			hoverEnterTimer.current = null;
		}

		if (hoverExitTimer.current) {
			clearTimeout(hoverExitTimer.current);
			hoverExitTimer.current = null;
		}
	};

	let mobileOverviewSwiper: OverviewMobileSwiperController | null = null;

	const syncColumns = () => {
		columns.forEach((column, index) => {
			column.update({
				decade: decades[index],
				isHovered: hoveredIndex === index,
				hoverScatterToken: hoveredIndex === index ? hoverScatterToken : 0,
				gridEntranceToken,
				activeCategoryKeys,
			});
		});

		mobileOverviewSwiper?.syncLoopClones();
	};

	const syncDetailViews = () => {
		detailViewCache.forEach((cachedDetailView, index) => {
			const decade = decades[index];
			if (!decade) {
				return;
			}

			cachedDetailView.update({
				decade,
				activeCategoryKeys,
				withEntrance: true,
			});
		});
	};

	const hideDetailViews = () => {
		detailViewCache.forEach((cachedDetailView) => {
			cachedDetailView.element.hidden = true;
		});
		detailView = null;
	};

	const destroyDetailViews = () => {
		detailViewCache.forEach((cachedDetailView) => {
			cachedDetailView.destroy();
		});
		detailViewCache.clear();
		detailView = null;
	};

	const showOverview = () => {
		selectedDecadeIndex = null;
		hoveredIndex = null;
		hideDetailViews();
		element.classList.remove("is-decade-selected");

		if (isMobileOverview()) {
			// Reset carousel position while still hidden so the first paint is already at decade 0.
			mobileOverviewSwiper?.prepareForOverviewReturn();
		} else {
			mobileOverviewSwiper?.collapseExpanded();
		}

		overviewHost.hidden = false;

		if (isMobileOverview()) {
			content.scrollIntoView({ block: "start", behavior: "auto" });
		}

		syncUrlFromModuleState();

		// Same entrance replay as desktop: divider + dot grid animations on every close.
		requestAnimationFrame(() => {
			gridEntranceToken += 1;
			animateDividerEntrance(trailingDivider);
			syncColumns();
			mobileOverviewSwiper?.finishOverviewReturn();
		});
	};

	const scrollToModuleTop = () => {
		element.scrollIntoView({
			block: "start",
			behavior: prefersReducedMotion() ? "auto" : "smooth",
		});
	};

	const showDetailView = (
		index: number,
		initialSelection?: {
			yearName?: string;
			eventIndex?: number;
		},
		options: { scrollIntoView?: boolean; focusOnOpen?: boolean } = {}
	) => {
		const decade = decades[index];
		if (!decade) {
			return;
		}

		clearHoverTimers();
		hoveredIndex = null;
		syncColumns();

		hideDetailViews();

		let nextDetailView = detailViewCache.get(index);
		if (!nextDetailView) {
			nextDetailView = createDecadeDetailView(decade, labels, showOverview, resetFilters, {
				initialActiveCategoryKeys: activeCategoryKeys,
				initialSelection,
				onStateChange: syncUrlFromModuleState,
				getShareUrl: getModuleShareUrl,
			});
			detailViewCache.set(index, nextDetailView);
			content.appendChild(nextDetailView.element);
		} else {
			nextDetailView.update({
				decade,
				activeCategoryKeys,
				withEntrance: true,
			});
		}

		detailView = nextDetailView;
		detailView.element.hidden = false;

		if (options.scrollIntoView !== false) {
			scrollToModuleTop();
		}

		if (options.focusOnOpen !== false) {
			requestAnimationFrame(() => {
				detailView?.element.focus({ preventScroll: true });
			});
		}

		selectedDecadeIndex = index;
		overviewHost.hidden = true;
		element.classList.add("is-decade-selected");
		syncUrlFromModuleState();
	};

	const scheduleHover = (index: number) => {
		if (selectedDecadeIndex !== null || isMobileOverview()) {
			return;
		}

		if (hoverExitTimer.current) {
			clearTimeout(hoverExitTimer.current);
			hoverExitTimer.current = null;
		}

		if (hoveredIndex === index) {
			return;
		}

		if (hoverEnterTimer.current) {
			clearTimeout(hoverEnterTimer.current);
			hoverEnterTimer.current = null;
		}

		hoverEnterTimer.current = setTimeout(() => {
			hoveredIndex = index;
			hoverScatterToken += 1;
			syncColumns();
			hoverEnterTimer.current = null;
		}, HOVER_ENTER_DELAY_MS);
	};

	const scheduleHoverClear = () => {
		if (selectedDecadeIndex !== null || isMobileOverview()) {
			return;
		}

		if (hoverEnterTimer.current) {
			clearTimeout(hoverEnterTimer.current);
			hoverEnterTimer.current = null;
		}

		if (hoverExitTimer.current) {
			clearTimeout(hoverExitTimer.current);
		}

		hoverExitTimer.current = setTimeout(() => {
			hoveredIndex = null;
			syncColumns();
			hoverExitTimer.current = null;
		}, HOVER_EXIT_DELAY_MS);
	};

	const focusDecade = (index: number) => {
		const column = columns[index];
		if (!column) {
			return;
		}

		column.element.focus({ preventScroll: true });
	};

	const activateHoveredDecade = (index: number) => {
		if (selectedDecadeIndex !== null) {
			return;
		}

		clearHoverTimers();
		hoveredIndex = index;
		hoverScatterToken += 1;
		syncColumns();
	};

	const centerDecade = (index: number) => {
		if (selectedDecadeIndex !== null) {
			return;
		}

		clearHoverTimers();

		if (isMobileOverview()) {
			if (hoveredIndex !== null && hoveredIndex !== index) {
				hoveredIndex = null;
				syncColumns();
			}

			mobileOverviewSwiper?.centerDecade(index, () => {
				activateHoveredDecade(index);
			});
			return;
		}

		activateHoveredDecade(index);
	};

	const handleDecadeFocusOut = (event: FocusEvent) => {
		if (selectedDecadeIndex !== null || isMobileOverview()) {
			return;
		}

		const nextTarget = event.relatedTarget;
		if (nextTarget instanceof Element && nextTarget.closest(".historical-timeline__decade")) {
			return;
		}

		scheduleHoverClear();
	};

	const handleSelectDecade = (index: number) => {
		showDetailView(index);
	};

	const columns: DecadeColumnController[] = decades.map((decade, index) => {
		const column = createDecadeColumn(
			decade,
			index,
			labels,
			() => handleSelectDecade(index),
			{
				onFocus: () => centerDecade(index),
				onNavigatePrevious: () => focusDecade(index - 1),
				onNavigateNext: () => focusDecade(index + 1),
				activateOnFirstClick: () => isMobileOverview(),
			}
		);

		column.element.addEventListener("mouseenter", () => scheduleHover(index));
		column.element.addEventListener("focusout", handleDecadeFocusOut);
		overviewHost.appendChild(column.element);

		return column;
	});

	const trailingDivider = document.createElement("div");
	trailingDivider.className = "historical-timeline__divider historical-timeline__content-divider";
	overviewHost.appendChild(trailingDivider);
	animateDividerEntrance(trailingDivider);

	syncColumns();

	const handleTimelineLeave = (event: MouseEvent) => {
		if (isMobileOverview()) {
			return;
		}

		const nextTarget = event.relatedTarget;
		if (nextTarget instanceof Node && overviewHost.contains(nextTarget)) {
			return;
		}

		scheduleHoverClear();
	};

	overviewHost.addEventListener("mouseleave", handleTimelineLeave);

	mobileOverviewSwiper = bindOverviewMobileSwiper({
		root: element,
		moduleRoot,
		contentHost: content,
		overviewHost,
		columnElements: columns.map((column) => column.element),
		onDeactivate: () => {
			hoveredIndex = null;
			syncColumns();
		},
	});

	const applyCategoryFilter = (categoryKeys: string[]) => {
		clearHoverTimers();
		hoveredIndex = null;
		hoverScatterToken = 0;
		mobileOverviewSwiper?.resetExpanded();

		activeCategoryKeys = categoryKeys;
		gridEntranceToken += 1;

		animateDividerEntrance(trailingDivider);
		syncColumns();
		syncDetailViews();
		syncUrlFromModuleState();
	};

	filterController = filtersRoot
		? bindTimelineFilter(
			filtersRoot,
			{
				all: labels.all,
				filters: labels.filters,
				removeFilter: labels.removeFilter,
			},
			applyCategoryFilter
		)
		: {
			destroy: () => undefined,
			reset: () => undefined,
			setCategoryKeys: () => undefined,
			getCategoryKeys: () => [],
			getAvailableCategoryKeys: () => [],
		};

	if (initialCategoryKeys.length > 0) {
		filterController.setCategoryKeys(initialCategoryKeys, false);
	}

	const initialDecadeIndex = findDecadeIndexByName(decades, initialUrlState.decadeName);
	if (initialDecadeIndex !== null) {
		const decade = decades[initialDecadeIndex];
		const initialSelection = resolveDetailSelection(
			decade,
			activeCategoryKeys,
			initialUrlState.yearName,
			initialUrlState.slideIndex
		);

		showDetailView(
			initialDecadeIndex,
			{
				yearName: initialSelection.yearName ?? undefined,
				eventIndex: initialSelection.eventIndex,
			},
			{ scrollIntoView: true, focusOnOpen: false }
		);
	} else {
		syncUrlFromModuleState();
	}

	return () => {
		clearHoverTimers();
		overviewHost.removeEventListener("mouseleave", handleTimelineLeave);
		mobileOverviewSwiper?.destroy();
		columns.forEach((column) => {
			column.element.removeEventListener("focusout", handleDecadeFocusOut);
			column.destroy();
		});
		filterController.destroy();
		destroyDetailViews();
		content.replaceChildren();
	};
};

export default HistoricalTimeline as Module<HistoricalTimelineViewModel>;
